import { useMemo, useState } from 'react'
import Button from '../ui/Button'
import FormError from '../ui/FormError'
import StarRatingDisplay from './StarRatingDisplay'
import StarRatingInput from './StarRatingInput'
import { localized } from '../../lib/i18n'
import type { CustomerBooking } from '../../graphql/customer'

interface ReviewFormCopy {
  ratingLabel: string
  commentLabel: string
  commentPlaceholder: string
  charCount: string
  submit: string
  poor: string
  belowAverage: string
  average: string
  good: string
  excellent: string
  schedule: string
  service: string
  property: string
  summary: string
  noProperty: string
  commentPreviewEmpty: string
  validationRating: string
  validationComment: string
}

const labelsByRating = {
  1: 'poor',
  2: 'belowAverage',
  3: 'average',
  4: 'good',
  5: 'excellent',
} as const

type RatingLabelKey = typeof labelsByRating[keyof typeof labelsByRating]

export default function ReviewForm({
  booking,
  locale,
  submitting,
  onSubmit,
  copy,
}: {
  booking: CustomerBooking
  locale: 'el' | 'en'
  submitting: boolean
  onSubmit: (input: { rating: number; comment: string }) => Promise<string[]>
  copy: ReviewFormCopy
}) {
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const ratingLabel = useMemo(() => {
    if (rating === 0) return null
    return copy[labelsByRating[rating as keyof typeof labelsByRating] as RatingLabelKey]
  }, [copy, rating])

  async function handleSubmit() {
    const nextErrors: string[] = []
    if (rating === 0) nextErrors.push(copy.validationRating)
    if (comment.trim().length > 0 && comment.trim().length < 20) nextErrors.push(copy.validationComment)

    if (nextErrors.length > 0) {
      setErrors(nextErrors)
      return
    }

    const mutationErrors = await onSubmit({ rating, comment: comment.trim() })
    setErrors(mutationErrors)
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
      <section className="rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-sm">
        <FormError errors={errors} />

        <div>
          <p className="text-sm font-semibold text-slate-700">{copy.ratingLabel}</p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <StarRatingInput value={rating} onChange={setRating} />
            {ratingLabel && <span className="text-sm font-semibold text-amber-700">{ratingLabel}</span>}
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between gap-3">
            <label htmlFor="review-comment" className="text-sm font-semibold text-slate-700">
              {copy.commentLabel}
            </label>
            <span className="text-xs font-medium text-slate-500">
              {comment.length}/1000 {copy.charCount}
            </span>
          </div>
          <textarea
            id="review-comment"
            rows={7}
            maxLength={1000}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            placeholder={copy.commentPlaceholder}
            className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100"
          />
        </div>

        <div className="mt-8 flex justify-end">
          <Button type="button" size="lg" loading={submitting} onClick={handleSubmit}>
            {copy.submit}
          </Button>
        </div>
      </section>

      <aside className="rounded-[1.75rem] border border-slate-200 bg-slate-50 p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">{copy.summary}</h2>
        <dl className="mt-5 space-y-4 text-sm text-slate-600">
          <div>
            <dt className="font-semibold text-slate-900">{copy.service}</dt>
            <dd className="mt-1">{localized(booking.serviceType, locale)}</dd>
          </div>
          <div>
            <dt className="font-semibold text-slate-900">{copy.schedule}</dt>
            <dd className="mt-1">
              {booking.scheduledDate} · {booking.startTime.slice(0, 5)} - {booking.endTime.slice(0, 5)}
            </dd>
          </div>
          <div>
            <dt className="font-semibold text-slate-900">{copy.property}</dt>
            <dd className="mt-1">
              {booking.property
                ? `${booking.property.label} · ${booking.property.addressLine1}, ${booking.property.city} ${booking.property.postalCode}`
                : copy.noProperty}
            </dd>
          </div>
        </dl>

        <div className="mt-6 rounded-[1.25rem] border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{copy.ratingLabel}</p>
          <div className="mt-3 flex items-center gap-2">
            <StarRatingDisplay rating={rating} />
            <span className="text-sm font-semibold text-slate-700">{ratingLabel ?? '—'}</span>
          </div>

          <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{copy.commentLabel}</p>
          <p className="mt-3 text-sm leading-7 text-slate-600">
            {comment.trim() || copy.commentPreviewEmpty}
          </p>
        </div>
      </aside>
    </div>
  )
}
